import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { isNilOrEmpty, isNotNilOrEmpty } from 'ramda-adjunct';

import Error from '../Error';
import arrow from './select_arrow.svg';

const propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
      unselectable: PropTypes.bool,
    })
  ),
  emptyOption: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onChangeIconFn: PropTypes.func,
};

const SelectInput = ({
  name,
  value = null,
  label = null,
  options: opts = [],
  emptyOption = null,
  onChangeIconFn = null,
  onSelect,
  error = null,
  required,
  ...rest
}) => {
  const [title, setTitle] = useState('');
  const [hasValue, setHasValue] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [labelSize, setLabelSize] = useState('normal');

  const refSelect = useRef(null);

  const options =
    opts?.length > 0 ? [{ value: null, label: ' ' }, ...opts] : [...opts];

  useEffect(() => {
    if (emptyOption) setTitle(emptyOption);
  }, []);

  useEffect(() => {
    if (options?.length > 0) {
      const curIndex = options.findIndex((opt) => opt.value === value);

      setHasValue(isNotNilOrEmpty(value));

      if (curIndex > -1)
        setTitle(
          options[curIndex].title
            ? options[curIndex].title
            : options[curIndex].label
        );
    }
  }, [options, value]);

  useEffect(() => {
    setLabelSize(
      isFocused || (!isFocused && title) || (title && !error && !rest.disabled)
        ? 'small'
        : 'normal'
    );
  }, [isFocused, title, error, rest.disabled]);

  const icon = useMemo(
    () => (onChangeIconFn && value ? onChangeIconFn(value) : {}),
    [value]
  );

  const onSelectChange = (e) => {
    if (onSelect) onSelect(e);
  };

  const onSelectFocus = (e) => {
    setIsFocused(true);
    if (rest.onFocus) rest.onFocus(e);
  };

  const onSelectBlur = (e) => {
    setIsFocused(false);
    setHasValue(!isNilOrEmpty(e.target.value));
    if (rest.onBlur) rest.onBlur(e);
  };

  // if (opts?.length === 0) return null;

  return (
    <div>
      <div
        className={cx('relative bg-white shadow-faq rounded-md', {
          'cursor-not-allowed': rest.disabled,
        })}
      >
        {label && (
          <label
            htmlFor={name}
            className={cx(
              'absolute pointer-events-none transition-all ease-out duration-200 z-20',
              {
                'text-gray-900': !error && !isFocused && !hasValue,
                'text-red-600': !!error && !isFocused,
                'text-cardif-green-light': labelSize === 'small',
                'text-xs left-3 top-1.5': labelSize === 'small',
                'left-4 top-[30%]': labelSize === 'normal',
              }
            )}
          >
            {label}
            {/* {required ? '*' : ''} */}
          </label>
        )}
        <div className="relative">
          <select
            {...rest}
            id={name}
            ref={refSelect}
            name={name}
            className={cx(
              'w-full pr-9 border-0 border-b-4 rounded-t-md rounded-b-sm focus:ring-0 outline-none focus:border-cardif-green-light',
              {
                'border-red-600': !!error,
                'border-cardif-green-light': hasValue || isFocused,
                'pl-9': !!icon?.URL,
                'py-5': !label,
                'mt-6': !!label,
              }
            )}
            value={value}
            required={required}
            onChange={onSelectChange}
            onFocus={onSelectFocus}
            onBlur={onSelectBlur}
          >
            {options.map((option) => {
              const optAttr = !option.value && { disabled: true, hidden: true };
              return (
                <option
                  disabled={option.unselectable}
                  key={`${option.value}-${option.label}`}
                  value={option.value}
                  {...optAttr}
                >
                  {option.label}
                </option>
              );
            })}
          </select>
          <div
            className={cx(
              'pointer-events-none absolute left-0 w-full pl-3 pr-2 flex items-center justify-between bg-white',
              {
                'top-1 h-[86%]': !label,
                'top-6 h-[50%]': !!label,
              }
            )}
          >
            {icon?.URL && (
              <div className="w-5 mr-2 -translate-y-[1px] max-h-[14px]">
                <img src={icon.URL} alt={icon.title} />
              </div>
            )}
            <div className="grow leading-3">{title}</div>
            <div>
              <img src={arrow} width="25px" />
            </div>
          </div>
        </div>
      </div>
      <Error error={error} />
    </div>
  );
};

SelectInput.propTypes = propTypes;

export default SelectInput;
